import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { not, or } from 'ember-truth-helpers';
import ComboBox from 'garaje/components/combo-box';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import preventDefault from 'garaje/helpers/prevent-default';
import type EmployeeModel from 'garaje/models/employee';
import type MacAddressMappingModel from 'garaje/models/mac-address-mapping';
import type StateService from 'garaje/services/state';
import employeesSearcherTask, { type EmployeeSearcherTask } from 'garaje/utils/employees-searcher';

const MAC_ADDRESS_REGEX = /^([0-9A-Fa-f]{2}[:-]?){5}[0-9A-Fa-f]{2}$/;

interface MacAddressMappingsModalSignature {
  Args: {
    mapping: MacAddressMappingModel | null;
    onSave: (data: { macAddress: string; email: string; employeeId?: string; employeeName?: string }) => void;
    onClose: () => void;
    isSaving: boolean;
    companyId: string;
  };
}

function normalizeMacAddress(value: string): string {
  const hex = value.replace(/[^0-9A-Fa-f]/g, '').toLowerCase();
  if (hex.length !== 12) return value;
  return hex.match(/.{2}/g)!.join(':');
}

export default class MacAddressMappingsModal extends Component<MacAddressMappingsModalSignature> {
  @service declare state: StateService;
  @service declare store: Store;

  @tracked macAddress = '';
  @tracked selectedEmployee: EmployeeModel | null = null;
  @tracked employees: EmployeeModel[] = [];
  @tracked initialEmployees: EmployeeModel[] = [];
  @tracked macAddressError = '';
  @tracked employeeError = '';
  @tracked didAttemptSubmit = false;

  private originalMacAddress = '';
  private originalEmployeeId: string | null = null;

  @(employeesSearcherTask({
    filter: { deleted: false, excludeHidden: true },
    prefix: false,
  }).restartable())
  declare searchEmployeesTask: EmployeeSearcherTask;

  constructor(owner: unknown, args: MacAddressMappingsModalSignature['Args']) {
    super(owner, args);

    if (args.mapping) {
      this.macAddress = args.mapping.macAddress ?? '';
      this.originalMacAddress = this.macAddress;
      this.originalEmployeeId = args.mapping.employeeId ? String(args.mapping.employeeId) : null;
    }

    void this.loadInitialEmployees.perform();
  }

  get isEditing(): boolean {
    return !!this.args.mapping;
  }

  get modalTitle(): string {
    return this.isEditing ? 'Edit mapping' : 'Add mapping';
  }

  get selectedEmployeeName(): string {
    return this.selectedEmployee?.name ?? '';
  }

  get hasChanges(): boolean {
    if (!this.isEditing) return true;
    const macChanged = normalizeMacAddress(this.macAddress) !== this.originalMacAddress;
    const employeeChanged = this.selectedEmployee?.id !== this.originalEmployeeId;
    return macChanged || employeeChanged;
  }

  get canSubmit(): boolean {
    return !!this.macAddress && !!this.selectedEmployee && !this.macAddressError && this.hasChanges;
  }

  loadInitialEmployees = task(async () => {
    const results = await this.store.query('employee', {
      page: { limit: 25, offset: 0 },
      filter: { deleted: false },
      sort: 'name',
    });
    this.initialEmployees = results.slice();
    this.employees = this.initialEmployees;

    // Pre-select employee when editing
    if (this.args.mapping?.employeeId) {
      const employeeId = String(this.args.mapping.employeeId);
      const found = this.initialEmployees.find((e) => e.id === employeeId);
      if (found) {
        this.selectedEmployee = found;
      } else {
        try {
          const employee = await this.store.findRecord('employee', employeeId, { backgroundReload: false });
          if (employee) {
            this.selectedEmployee = employee;
          }
        } catch {
          // Employee not found — leave unselected
        }
      }
    }
  });

  onEmployeeSearchTask = task(async (term: string) => {
    const extraFilters = {
      locations: this.state.currentLocation?.id,
    };
    if (term) {
      this.employees = await this.searchEmployeesTask.perform(term, extraFilters);
    } else {
      this.employees = this.initialEmployees;
    }
  });

  @action
  returnFalseValid(): boolean {
    return false;
  }

  @action
  onEmployeeSelect(employee: EmployeeModel | string): void {
    if (typeof employee === 'string' || employee === undefined) return;
    this.selectedEmployee = employee;
    this.employeeError = '';
  }

  @action
  onEmployeeFocusOut(): void {
    // Revert to last selected employee name when clicking away
    // The ComboBox will show the @selected value again
  }

  @action
  onMacAddressInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.macAddress = target.value;
    this.validateMacAddress();
  }

  @action
  onMacAddressBlur(): void {
    if (this.macAddress) {
      this.macAddress = normalizeMacAddress(this.macAddress);
      this.validateMacAddress();
    }
  }

  @action
  validateMacAddress(): void {
    if (!this.macAddress) {
      this.macAddressError = this.didAttemptSubmit ? 'MAC address is required' : '';
      return;
    }

    const hex = this.macAddress.replace(/[^0-9A-Fa-f:-]/g, '');
    const hasInvalidChars = hex !== this.macAddress;

    const normalized = normalizeMacAddress(this.macAddress);
    if (hasInvalidChars) {
      this.macAddressError = 'MAC address can only contain hex characters (0-9, A-F) and separators (: or -)';
    } else if (!MAC_ADDRESS_REGEX.test(normalized)) {
      this.macAddressError = 'MAC address must be 12 hex characters (e.g., aa:bb:cc:dd:ee:ff)';
    } else {
      this.macAddressError = '';
    }
  }

  @action
  handleSubmit(): void {
    this.didAttemptSubmit = true;
    this.validateMacAddress();

    if (!this.selectedEmployee) {
      this.employeeError = 'Employee is required';
    }

    if (!this.canSubmit) return;

    const normalized = normalizeMacAddress(this.macAddress);
    this.args.onSave({
      macAddress: normalized,
      email: this.selectedEmployee!.email,
      employeeId: this.selectedEmployee!.id,
      employeeName: this.selectedEmployee!.name,
    });
  }

  <template>
    <ModalDialog
      @targetAttachment="center"
      @translucentOverlay={{true}}
      @containerClass="ember-modal-dialog--header-modal !max-w-[500px] !overflow-visible"
    >
      <div class="p-6" data-test-mac-mapping-modal>
        <h2 class="mb-4 text-xl font-semibold">{{this.modalTitle}}</h2>

        <form {{on "submit" (preventDefault this.handleSubmit)}}>
          <div class="mb-4">
            <label class="mb-1 block text-sm font-medium text-carbon-70">
              Employee
            </label>
            <ComboBox
              data-test-employee-dropdown
              @activeOptionClass="text-gem-60 bg-gem-10"
              @emptyOption={{if this.loadInitialEmployees.isRunning "Loading employees..." "No employee found"}}
              @inputClass="input unset-global-margin !rounded-md !max-w-full {{if this.employeeError 'border-red-50'}}"
              @inputId="employee-input"
              @isLoading={{this.loadInitialEmployees.isRunning}}
              @listboxClass="absolute z-10 bg-white overflow-y-auto max-h-64 rounded-lg shadow w-full"
              @onInput={{this.onEmployeeSearchTask.perform}}
              @onFocusOut={{this.onEmployeeFocusOut}}
              @onSelect={{this.onEmployeeSelect}}
              @options={{this.employees}}
              @optionClass="py-2 pl-3 pr-3 outline-none cursor-pointer hover:text-gem-50 hover:bg-gem-5"
              @placeholder="Search by name or email..."
              @scrollToSelection={{false}}
              @selected={{this.selectedEmployeeName}}
              @selectedOptionClass="text-gem-60 bg-gem-10 hover:text-gem-60 hover:bg-gem-10"
              @type="search"
              @validateUserInput={{this.returnFalseValid}}
              as |employee|
            >
              <div class="flex justify-between items-center" data-test-employee-option>
                {{! @glint-expect-error employee is EmployeeModel here }}
                {{employee.name}}
              </div>
            </ComboBox>
            {{#if this.employeeError}}
              <p class="mt-1 text-xs text-red-50" data-test-employee-error>
                {{this.employeeError}}
              </p>
            {{/if}}
          </div>

          <div class="mb-4">
            <label class="mb-1 block text-sm font-medium text-carbon-70" for="mac-address-input">
              MAC address
            </label>
            <input
              id="mac-address-input"
              type="text"
              value={{this.macAddress}}
              placeholder="aa:bb:cc:dd:ee:ff"
              class="w-full rounded border px-3 py-2 text-sm
                {{if this.macAddressError 'border-red-50' 'border-carbon-20'}}
                focus:border-blue-50 focus:outline-none"
              data-test-mac-address-input
              {{on "input" this.onMacAddressInput}}
              {{on "blur" this.onMacAddressBlur}}
            />
            {{#if this.macAddressError}}
              <p class="mt-1 text-xs text-red-50" data-test-mac-address-error>
                {{this.macAddressError}}
              </p>
            {{/if}}
          </div>

          <div class="flex justify-end gap-3 pt-4">
            <button type="button" class="btn btn--small btn--outline" data-test-cancel-btn {{on "click" @onClose}}>
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn--small btn--brand"
              disabled={{or (not this.canSubmit) @isSaving}}
              data-test-save-btn
            >
              {{if @isSaving "Saving..." "Save"}}
            </button>
          </div>
        </form>
      </div>
    </ModalDialog>
  </template>
}
